import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
} from '@mui/material';
import { UsersTable, User } from '../../components/Tables';
import { Tabs, Modal } from '../../components/Common';
import { PageHeader } from '../../components/Layout';
import { AddUserForm, EditUserForm } from '../../components/Forms/Admin';
import { useAdmins } from './useAdmins';
import { filterAdmins } from './Admins.utils';

const Admins: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'suspended'>('all');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Fetch admins data
  const { users, loading, refetch } = useAdmins();
  
  // Get current user email and role
  const currentUserEmail = localStorage.getItem('userEmail') || undefined;
  const currentUserRole = localStorage.getItem('userRole') as 'admin' | 'super_admin' | null;
  const isSuperAdmin = currentUserRole === 'super_admin';
  
  // Filter admins based on search and tab
  const filteredUsers = filterAdmins(users, searchQuery, activeTab);

  // Handle edit user
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };


  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Page Header */}
      <PageHeader
        title="Admins"
        showSearch
        searchPlaceholder="Search admins"
        searchValue={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
        {...(isSuperAdmin && {
          actionButtonText: "Add admin",
          onActionClick: () => setIsAddUserModalOpen(true)
        })}
      />

      {/* Add Admin Modal */}
      <Modal
        open={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        title="Add admin"
        width={508}
      >
        <AddUserForm
          onSuccess={() => {
            setIsAddUserModalOpen(false);
            refetch();
          }}
          onCancel={() => setIsAddUserModalOpen(false)}
        />
      </Modal>

      {/* Edit Admin Modal */}
      <Modal
        open={isEditUserModalOpen}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setSelectedUser(null);
        }}
        title="Edit admin"
        width={508}
      >
        {selectedUser && (
          <EditUserForm
            adminId={selectedUser.adminId}
            initialName={selectedUser.name}
            initialEmail={selectedUser.email}
            onSuccess={() => {
              setIsEditUserModalOpen(false);
              setSelectedUser(null);
              refetch();
            }}
            onCancel={() => {
              setIsEditUserModalOpen(false);
              setSelectedUser(null);
            }}
          />
        )}
      </Modal>

      {/* Tabs and Table Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', gap: '32px', width: '100%', flex: 1, overflow: 'hidden' }}>
        {/* Tabs */}
        <Tabs
          tabs={[
            { key: 'all', label: 'All' },
            { key: 'active', label: 'Active' },
            { key: 'suspended', label: 'Suspended' },
          ]}
          activeTab={activeTab}
          onTabChange={(tabKey) => setActiveTab(tabKey as typeof activeTab)}
        />

        {/* Table with Pagination */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
          </Box>
        ) : (
          <UsersTable 
            users={filteredUsers} 
            onUserUpdated={refetch}
            onEditUser={handleEditUser}
            currentUserEmail={currentUserEmail}
            currentUserRole={currentUserRole}
          />
        )}
      </Box>
    </Box>
  );
};

export default Admins;
